/**
 * useNotification Hook
 * 
 * Type-safe hook for accessing notification context with error handling
 * 
 * Features:
 * - Clean API for notification access
 * - Automatic error handling
 * - Memoized selectors
 * - Type safety
 * 
 * Usage:
 * ```jsx
 * import useNotification from '../hooks/useNotification';
 * 
 * function NotificationBadge() {
 *   const { unreadCount, markAllAsRead } = useNotification();
 *   
 *   return (
 *     <button onClick={markAllAsRead}>
 *       Notifications ({unreadCount})
 *     </button>
 *   );
 * }
 * ```
 */

import { useNotifications } from '../contexts/NotificationContext';

/**
 * Hook to access notification functionality
 * @returns {Object} Notification context
 */
const useNotification = () => {
  const context = useNotifications();
  
  if (!context) {
    throw new Error('useNotification must be used within a NotificationProvider');
  }
  
  return context;
};

export default useNotification;
